import React from 'react'
import { generatePath, matchPath } from 'react-router-dom'
import type { StylesConfig } from 'react-select'
import type { Option } from '@components/Form/Select'
import i18n from 'i18next'
import routes from '@msteams/Routes/routes'

import { ContactParty } from '@models/ApproveitAccounting'
import type { UIFeatures } from '@models/Account'
import type { LinkGroupData, LinkGroupOption, SingleLinkObject } from '@msteams/components/Navigation/types'
import { LinkGroupType } from '@msteams/components/Navigation/types'
import { IconLock, IconSettings } from '@tabler/icons-react'
import { Feature } from '@type/features'
import { FeaturesPaywall } from '@helpers/hooks/useGetHideWithPaywall'
import { Perm, TEAM_SETTINGS_PERMS } from '@type/permissions'
import type { PermCodename } from '@type/permissions'
// Workspace feature toggle (Settings → Features) — checked BEFORE the perm
// gate, mirroring the web nav's filterNavItems; see the shared helper for
// the compose rule.
import { isUIFeatureVisible } from '@helpers/uiFeatureVisibility'

// Perm gate — bare presence check against the user's resolved
// permission set. Backend has_perm() expands deprecated aliases, so
// the frontend `permissions` array already contains the granular
// view codenames even for roles that hold legacy `accounting.view`.
const hasPerm = (perms: Set<string>, codename: PermCodename): boolean => perms.has(codename)

export const LinkGroupTitles: Record<LinkGroupType, string> = {
  [LinkGroupType.payable]: i18n.t('common:navigation.accountsPayable'),
  [LinkGroupType.receivable]: i18n.t('common:navigation.accountsReceivable'),
}

export const LinkGroups: LinkGroupData[] = [
  {
    getOptions: (perms: Set<string>, uiFeatures?: UIFeatures, isAdmin = false): LinkGroupOption[] => {
      const options: LinkGroupOption[] = []

      if (isUIFeatureVisible('bills', isAdmin, uiFeatures) && hasPerm(perms, Perm.BILL_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.bills'),
          value: routes.private.bills,
        })
      }
      if (isUIFeatureVisible('purchase_orders', isAdmin, uiFeatures) && hasPerm(perms, Perm.PURCHASE_ORDER_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.purchaseOrders'),
          value: routes.private.purchaseOrders,
        })
      }
      if (isUIFeatureVisible('rfqs', isAdmin, uiFeatures) && hasPerm(perms, Perm.RFQ_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.rfq'),
          value: routes.private.rfqs,
        })
      }
      if (isUIFeatureVisible('vendors', isAdmin, uiFeatures) && hasPerm(perms, Perm.VENDOR_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.vendors'),
          value: generatePath(routes.private.contacts, { contactParty: ContactParty.vendor }),
        })
      }
      return options
    },
    isActive: (path: string) => {
      return !![
        routes.private.purchaseOrders,
        routes.private.purchaseOrdersById,
        routes.private.bills,
        routes.private.billById,
        routes.private.contactsList,
        routes.private.contact,
        routes.private.contactContract,
        routes.private.contactContracts,
        routes.private.rfqs,
        routes.private.rfqProposalsById,
        routes.private.rfqById,
        routes.private.costCenter,
        routes.private.costCenterById,
        routes.private.costCenterBudget,
        routes.private.costCenterEditById,
        routes.private.financeSettings,
        routes.private.accountCategories,
        routes.private.financeSettings,
        routes.private.accounts,
        routes.private.accountById,
        routes.private.accountCategories,
        routes.private.accountCategoryById,
        routes.private.companyDetails,
        routes.private.taxes,
        routes.private.taxById,
        routes.private.products,
        routes.private.productById,
        generatePath(routes.private.paywall, { feature: FeaturesPaywall.CostCenters }),
      ].find((route) => matchPath(route, path))
    },
    type: LinkGroupType.payable,
    feature: Feature.accountsPayable,
  },
  {
    getOptions: (perms: Set<string>, uiFeatures?: UIFeatures, isAdmin = false): LinkGroupOption[] => {
      const options: LinkGroupOption[] = []

      if (isUIFeatureVisible('invoices', isAdmin, uiFeatures) && hasPerm(perms, Perm.INVOICE_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.invoices'),
          value: routes.private.invoices,
        })
      }
      if (isUIFeatureVisible('quotes', isAdmin, uiFeatures) && hasPerm(perms, Perm.QUOTE_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.quotes'),
          value: routes.private.quotes,
        })
      }
      if (isUIFeatureVisible('cost_centers', isAdmin, uiFeatures) && hasPerm(perms, Perm.COST_CENTER_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.costCenters'),
          value: routes.private.costCenter,
          feature: Feature.costCenters,
        })
      }
      if (isUIFeatureVisible('customers', isAdmin, uiFeatures) && hasPerm(perms, Perm.CUSTOMER_VIEW)) {
        options.push({
          label: i18n.t('common:navigation.customers'),
          value: generatePath(routes.private.contacts, { contactParty: ContactParty.customer }),
        })
      }
      return options
    },
    isActive: (path: string) => {
      return !![
        routes.private.invoices,
        routes.private.invoiceById,
        routes.private.quotes,
        routes.private.quoteById,
        routes.private.contactsList,
        routes.private.contact,
        routes.private.contactContract,
        routes.private.costCenter,
        routes.private.costCenterNew,
        routes.private.costCenterById,
        routes.private.costCenterEditById,
        routes.private.costCenterBudget,
        routes.private.costCenterBreakdown,
      ].find((route) => matchPath(route, path))
    },
    feature: Feature.accountsReceivable,
    type: LinkGroupType.receivable,
  },
]

export const getSingleLinks = (
  perms: Set<string>,
  aiChatbotEnabled?: boolean,
  aiAssistantHidden?: boolean,
  uiFeatures?: UIFeatures,
  isAdmin = false,
): SingleLinkObject[] => {
  const links = [
    // Settings - always visible
    {
      to: routes.private.settings,
      label: <IconSettings />,
      isActive: (path: string) => !![routes.private.settingsRouted].find((match) => matchPath(match, path)),
    },
    // Approval Requests - always visible
    {
      to: routes.private.approvalRequests,
      label: i18n.t('common:navigation.approvalRequests'),
      isActive: (path: string) =>
        !![
          routes.private.approvalRequests,
          routes.private.approvalRequests,
          routes.private.approvalRequestsById,
          routes.private.approvalRequestsByIdAction,
          routes.private.approvalRequest,
          routes.private.approvalRequestEditById,
        ].find((match) => matchPath(match, path)),
    },
    // AI Assistant - visible for everyone unless the team opted out via
    // team.settings.ai_assistant_hidden (escape hatch for AI-averse
    // clients); with ai_chatbot_enabled off it shows a lock and /assistant
    // renders the beta pitch instead of the chat
    aiAssistantHidden
      ? null
      : {
          to: routes.private.assistant,
          label: aiChatbotEnabled ? (
            i18n.t('common:navigation.aiAssistant')
          ) : (
            <span className="flex items-center gap-1.5" title={i18n.t('common:navigation.aiAssistantLockedTooltip')}>
              <>
                {i18n.t('common:navigation.aiAssistant')}
                <IconLock
                  className="h-4 w-4 text-amber-700"
                  stroke={1.75}
                  aria-label={i18n.t('common:navigation.aiAssistantLockedTooltip')}
                />
              </>
            </span>
          ),
          isActive: (path: string) => !!matchPath(`${routes.private.assistant}/*`, path),
        },
    isUIFeatureVisible('workflows', isAdmin, uiFeatures) && hasPerm(perms, Perm.WORKFLOW_VIEW)
      ? {
          to: routes.private.pipelines,
          label: i18n.t('common:navigation.workflows'),
          isActive: (path: string) =>
            !![
              routes.private.processes,
              routes.private.pipelineProcesses,
              routes.private.pipelines,
              routes.private.pipelineProcessesById,
              routes.private.pipelineProcesses,
              routes.private.pipelineApprovalRequest,
              routes.private.pipelineById,
            ].find((match) => matchPath(match, path)),
        }
      : null,
    isUIFeatureVisible('tasks', isAdmin, uiFeatures) && hasPerm(perms, Perm.TASK_VIEW)
      ? {
          to: routes.private.tasks,
          label: i18n.t('common:navigation.tasks'),
          isActive: (path: string) =>
            !!(matchPath(routes.private.tasks, path) || matchPath(routes.private.taskBoards, path)),
        }
      : null,
    isUIFeatureVisible('reports', isAdmin, uiFeatures) && hasPerm(perms, Perm.REPORT_VIEW)
      ? {
          to: routes.private.reports,
          label: i18n.t('common:navigation.reports'),
          isActive: (path: string) =>
            !![
              routes.private.reports,
              routes.private.aiReports,
              routes.private.createReport,
              routes.private.reportById,
            ].find((match) => matchPath(match, path)),
        }
      : null,
    isUIFeatureVisible('mailbox', isAdmin, uiFeatures) && hasPerm(perms, Perm.MAILBOX_VIEW)
      ? {
          to: routes.private.mailbox,
          label: i18n.t('common:navigation.mailbox'),
          isActive: (path: string) => !!matchPath(routes.private.mailbox, path),
          feature: Feature.mailbox,
        }
      : null,
    // Team area hosts Members (team.view), Departments/Locations
    // (team.manage_org_structure) and Roles (role.view). Show the entry for
    // any of them so a role-admin without team.view can still reach Roles —
    // mirrors the web nav's requireAnyPerm and the shared TeamHome redirect.
    isUIFeatureVisible('team', isAdmin, uiFeatures) &&
    (hasPerm(perms, Perm.TEAM_VIEW) || hasPerm(perms, Perm.TEAM_MANAGE_ORG_STRUCTURE) || hasPerm(perms, Perm.ROLE_VIEW))
      ? {
          to: routes.private.organizationalStructure,
          label: i18n.t('common:navigation.team'),
          isActive: (path: string) =>
            !![
              routes.private.organizationalStructure,
              routes.private.teamDepartments,
              routes.private.teamDepartmentById,
              routes.private.teamLocations,
              routes.private.teamLocationById,
              routes.private.teamRoles,
            ].find((match) => matchPath(match, path)),
        }
      : null,
    // Workspace (Team) Settings — mirror of the web AccountMenu entry so the
    // Teams embed exposes the same settings. Reachable by any of its tabs'
    // perms (team.manage_settings / integration.view / audit_log.view).
    TEAM_SETTINGS_PERMS.some((p) => perms.has(p))
      ? {
          to: routes.private.teamSettings,
          label: i18n.t('common:navigation.teamSettings'),
          isActive: (path: string) =>
            !![routes.private.teamSettings, routes.private.teamSettingsRouted].find((match) => matchPath(match, path)),
        }
      : null,
    isUIFeatureVisible('contracts', isAdmin, uiFeatures) && hasPerm(perms, Perm.CONTRACT_VIEW)
      ? {
          to: routes.private.contracts,
          label: i18n.t('common:navigation.contracts'),
          isActive: (path: string) => !!matchPath(routes.private.contracts, path),
          feature: Feature.contractManagement,
        }
      : null,
  ]

  return links.filter(Boolean) as SingleLinkObject[]
}

export const LinkGroupStylesConfig: StylesConfig<Option<string>, false> = {
  control: (base, props) => {
    return {
      ...base,
      cursor: 'pointer',
      backgroundColor: props.selectProps.value ? ' rgb(250, 245, 255,1)' : base.backgroundColor,
      borderRadius: 8,
      borderWidth: '2px',
      borderColor: 'rgb(229 231 235)',
      '&:hover': {
        borderColor: 'rgb(216 180 254 )',
      },
    }
  },
  dropdownIndicator: (base, props) => ({
    ...base,
    transform: props.selectProps.menuIsOpen ? 'rotate(180deg)' : '',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(55, 65, 81, 1)',
    fontWeight: '500',
  }),
  singleValue: (base, props) => ({
    ...base,
    color: props.selectProps.value ? ' rgba(67, 56, 202, 1)' : 'black',
    fontWeight: '500',
  }),
  valueContainer: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
  }),
}
